import React from "react";
import { Box } from "@mui/material";
import "bootstrap/dist/css/bootstrap.min.css";

import { useRiskAnalysisController } from "./hooks/useRiskAnalysisController";
import RiskAnalysisHeader from "./components/RiskAnalysisHeader";
import RiskTabFilters from "./components/RiskTabFilters";
import DownloadPanel from "./components/DownloadPanel";
import RiskAnalysisTabContent from "./components/RiskAnalysisTabContent";
import HiddenExportLayers from "./components/HiddenExportLayers";

const RiskAnalysis = () => {
  const c = useRiskAnalysisController();

  const csvHandlers = {
    faktuel: () => c.handleDownloadRiskCsv(),
    reportingCategory: () => c.handleDownloadReportingCsv(),
    performance: () => c.handleDownloadRiskCsv(),
    mission: () => c.downloadCsv(c.missionFiltered, "mission.csv"),
    guest: () => c.downloadCsv(c.guestFiltered, "guest.csv"),
    estimated: () => c.downloadCsv(c.estimatedFiltered, "estimated.csv"),
  };

  return (
    <Box id="risk-analysis-root" className="risk-analysis-page bg-white p-0">
      <Box className="risk-analysis-shell">
        <Box className="risk-analysis-top-grid">
          <Box className="risk-analysis-filter-card">
            <RiskAnalysisHeader
              orgLoading={c.orgLoading}
              orgName={c.orgName}
              lang={c.lang}
              setLang={c.setLang}
            />
            <RiskTabFilters
              activeTabKey={c.activeTabKey}
              t={c.t}
              canDate={c.canDate}
              canTime={c.canTime}
              canSet={c.canSet}
              canEmployee={c.canEmployee}
              canDownload={c.canDownload}
              showReportingCategory={c.showReportingCategory}
              pdfDownloading={c.pdfDownloading}
              pngDownloading={c.pngDownloading}
              csvDownloading={c.csvDownloading}
              isRiskDownloadBusy={c.isRiskDownloadBusy}
              onDownloadPdf={c.handleDownloadPdf}
              onDownloadPng={c.handleDownloadPng}
              onDownloadRiskCsv={c.handleDownloadRiskCsv}
              onDownloadReportingCsv={c.handleDownloadReportingCsv}
              downloadCsv={c.downloadCsv}
              missionFiltered={c.missionFiltered}
              guestFiltered={c.guestFiltered}
              estimatedFiltered={c.estimatedFiltered}
              estimatedData={c.estimatedData}
              dateFrom={c.dateFrom}
              setDateFrom={c.setDateFrom}
              dateTo={c.dateTo}
              setDateTo={c.setDateTo}
              timeRange={c.timeRange}
              setTimeRange={c.setTimeRange}
              allowedTimeRange={c.allowedTimeRange}
              setNumbers={c.setNumbers}
              setSetNumbers={c.setSetNumbers}
              realIncidentSetOptions={c.realIncidentSetOptions}
              employees={c.employees}
              setEmployees={c.setEmployees}
              employeeOptions={c.employeeOptions}
              rDateFrom={c.rDateFrom}
              setRDateFrom={c.setRDateFrom}
              rDateTo={c.rDateTo}
              setRDateTo={c.setRDateTo}
              rTimeRange={c.rTimeRange}
              setRTimeRange={c.setRTimeRange}
              rSetNumbers={c.rSetNumbers}
              setRSetNumbers={c.setRSetNumbers}
              reportingCategorySetOptions={c.reportingCategorySetOptions}
              rEmployees={c.rEmployees}
              setREmployees={c.setREmployees}
              mDateFrom={c.mDateFrom}
              setMDateFrom={c.setMDateFrom}
              mDateTo={c.mDateTo}
              setMDateTo={c.setMDateTo}
              mTimeRange={c.mTimeRange}
              setMTimeRange={c.setMTimeRange}
              mSetNumbers={c.mSetNumbers}
              setMSetNumbers={c.setMSetNumbers}
              missionSetOptions={c.missionSetOptions}
              gDateFrom={c.gDateFrom}
              setGDateFrom={c.setGDateFrom}
              gDateTo={c.gDateTo}
              setGDateTo={c.setGDateTo}
              gTimeRange={c.gTimeRange}
              setGTimeRange={c.setGTimeRange}
              gSetNumbers={c.gSetNumbers}
              setGSetNumbers={c.setGSetNumbers}
              eDateFrom={c.eDateFrom}
              setEDateFrom={c.setEDateFrom}
              eDateTo={c.eDateTo}
              setEDateTo={c.setEDateTo}
              eTimeRange={c.eTimeRange}
              setETimeRange={c.setETimeRange}
              eSetNumbers={c.eSetNumbers}
              setESetNumbers={c.setESetNumbers}
              estimatedSetOptions={c.estimatedSetOptions}
              eBeach={c.eBeach}
              setEBeach={c.setEBeach}
            />
          </Box>
          <DownloadPanel
            activeTabKey={c.activeTabKey}
            t={c.t}
            canDownload={c.canDownload}
            pdfDownloading={c.pdfDownloading}
            pngDownloading={c.pngDownloading}
            csvDownloading={c.csvDownloading}
            isRiskDownloadBusy={c.isRiskDownloadBusy}
            onDownloadPdf={c.handleDownloadPdf}
            onDownloadPng={c.handleDownloadPng}
            csvHandlers={csvHandlers}
          />
        </Box>

        <RiskAnalysisTabContent
          activeTabKey={c.activeTabKey}
          tab={c.tab}
          setTab={c.setTab}
          tabDefs={c.tabDefs}
          lang={c.lang}
          organizationId={c.organizationId}
          isPrototypeGrouped={c.isPrototypeGrouped}
          incidentChartGroups={c.incidentChartGroups}
          reportingPrototypeGroups={c.reportingPrototypeGroups}
          prototypeChartGroups={c.prototypeChartGroups}
          incidentRef={c.incidentRef}
          tPerformanceRef={c.tPerformanceRef}
          missionRef={c.missionRef}
          guestRef={c.guestRef}
          estimatedRef={c.estimatedRef}
          assignNodeRef={c.assignNodeRef}
          incidentChartRefs={c.incidentChartRefs}
          tPerformanceChartRefs={c.tPerformanceChartRefs}
          showRealIncident={c.showRealIncident}
          showIncidentYellow={c.showIncidentYellow}
          showIncidentRed={c.showIncidentRed}
          includeTrainingData={c.includeTrainingData}
          includeReadData={c.includeReadData}
          showMinInjuries={c.showMinInjuries}
          setShowMinInjuries={c.setShowMinInjuries}
          setShowRealIncident={c.setShowRealIncident}
          showTraining={c.showTraining}
          setShowTraining={c.setShowTraining}
          showRead={c.showRead}
          setShowRead={c.setShowRead}
          showNull={c.showNull}
          setShowNull={c.setShowNull}
          setShowIncidentYellow={c.setShowIncidentYellow}
          setShowIncidentRed={c.setShowIncidentRed}
          isGentofteLife={c.isGentofteLife}
          t={c.t}
          riskRecordsLoading={c.riskRecordsLoading}
          actionCardsLoading={c.actionCardsLoading}
          missionGuestLoading={c.missionGuestLoading}
          missionGuestError={c.missionGuestError}
          estimatedLoading={c.estimatedLoading}
          estimatedError={c.estimatedError}
          missionFiltered={c.missionFiltered}
          guestFiltered={c.guestFiltered}
          estimatedFiltered={c.estimatedFiltered}
          mDateFrom={c.mDateFrom}
          mDateTo={c.mDateTo}
          mTimeRange={c.mTimeRange}
          mSetNumbers={c.mSetNumbers}
          mShowGreen={c.mShowGreen}
          mShowYellow={c.mShowYellow}
          mShowRed={c.mShowRed}
          setMDateFrom={c.setMDateFrom}
          setMDateTo={c.setMDateTo}
          setMTimeRange={c.setMTimeRange}
          setMSetNumbers={c.setMSetNumbers}
          setMShowGreen={c.setMShowGreen}
          setMShowYellow={c.setMShowYellow}
          setMShowRed={c.setMShowRed}
          gDateFrom={c.gDateFrom}
          gDateTo={c.gDateTo}
          gTimeRange={c.gTimeRange}
          gSetNumbers={c.gSetNumbers}
          setGDateFrom={c.setGDateFrom}
          setGDateTo={c.setGDateTo}
          setGTimeRange={c.setGTimeRange}
          setGSetNumbers={c.setGSetNumbers}
          eDateFrom={c.eDateFrom}
          eDateTo={c.eDateTo}
          eTimeRange={c.eTimeRange}
          eSetNumbers={c.eSetNumbers}
          eBeach={c.eBeach}
          setEDateFrom={c.setEDateFrom}
          setEDateTo={c.setEDateTo}
          setETimeRange={c.setETimeRange}
          setESetNumbers={c.setESetNumbers}
          setEBeach={c.setEBeach}
          rDateFrom={c.rDateFrom}
          rDateTo={c.rDateTo}
          rShowNearMiss={c.rShowNearMiss}
          rShowPrevention={c.rShowPrevention}
          rShowDoubtAboutReporting={c.rShowDoubtAboutReporting}
          renderLoader={c.renderLoader}
          tableRef={c.tableRef}
          riskPdfTableRecords={c.riskPdfTableRecords}
          actionCards={c.actionCards}
        />

        <HiddenExportLayers
          incidentChartGroups={c.incidentChartGroups}
          assignNodeRef={c.assignNodeRef}
          incidentImageExportRefs={c.incidentImageExportRefs}
          incidentReportingImageExportRefs={c.incidentReportingImageExportRefs}
          tPerformanceImageExportRefs={c.tPerformanceImageExportRefs}
          incidentExportRefs={c.incidentExportRefs}
          incidentReportingExportRefs={c.incidentReportingExportRefs}
          tPerformanceExportRefs={c.tPerformanceExportRefs}
          missionExportRef={c.missionExportRef}
          guestExportRef={c.guestExportRef}
          estimatedExportRef={c.estimatedExportRef}
          showReportingCategory={c.showReportingCategory}
          hasRiskReportingCategoryFilter={c.hasRiskReportingCategoryFilter}
          isPrototypeGrouped={c.isPrototypeGrouped}
          lang={c.lang}
          organizationId={c.organizationId}
          showRealIncident={c.showRealIncident}
          showIncidentYellow={c.showIncidentYellow}
          showIncidentRed={c.showIncidentRed}
          includeTrainingData={c.includeTrainingData}
          includeReadData={c.includeReadData}
          dateFrom={c.dateFrom}
          dateTo={c.dateTo}
          buildImageHeaderRows={c.buildImageHeaderRows}
          buildRiskImageDetailRows={c.buildRiskImageDetailRows}
          buildReportingImageDetailRows={c.buildReportingImageDetailRows}
          buildPerformanceImageDetailRows={c.buildPerformanceImageDetailRows}
          missionFiltered={c.missionFiltered}
          guestFiltered={c.guestFiltered}
          estimatedFiltered={c.estimatedFiltered}
          mDateFrom={c.mDateFrom}
          mDateTo={c.mDateTo}
          mTimeRange={c.mTimeRange}
          mSetNumbers={c.mSetNumbers}
          mShowGreen={c.mShowGreen}
          mShowYellow={c.mShowYellow}
          mShowRed={c.mShowRed}
          setMDateFrom={c.setMDateFrom}
          setMDateTo={c.setMDateTo}
          setMTimeRange={c.setMTimeRange}
          setMSetNumbers={c.setMSetNumbers}
          setMShowGreen={c.setMShowGreen}
          setMShowYellow={c.setMShowYellow}
          setMShowRed={c.setMShowRed}
          gDateFrom={c.gDateFrom}
          gDateTo={c.gDateTo}
          gTimeRange={c.gTimeRange}
          gSetNumbers={c.gSetNumbers}
          setGDateFrom={c.setGDateFrom}
          setGDateTo={c.setGDateTo}
          setGTimeRange={c.setGTimeRange}
          setGSetNumbers={c.setGSetNumbers}
          eDateFrom={c.eDateFrom}
          eDateTo={c.eDateTo}
          eTimeRange={c.eTimeRange}
          eSetNumbers={c.eSetNumbers}
          eBeach={c.eBeach}
          setEDateFrom={c.setEDateFrom}
          setEDateTo={c.setEDateTo}
          setETimeRange={c.setETimeRange}
          setESetNumbers={c.setESetNumbers}
          setEBeach={c.setEBeach}
        />
      </Box>
    </Box>
  );
};

export default RiskAnalysis;
